import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Empty, Form, Input, message, Radio, Select, Spin } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { BackButton, BasicSettingWrapper, CustomSettingWrapper, FormButton, SettingWrapper, SubTitle } from '../segment.styled'
import { fetchSources } from '../../../apis/source'
import CriteriaSetting from './CriteriaSetting'
import { basicConf, categoryConf, customReqBody } from './config/srcConf'
import { useSelector } from 'react-redux'
import { addCriteria, modCriteria } from '../../../apis/segment'

function CriteriaForm (props) {
  const { goBackPath, addMode, editMode } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}
  const user = useSelector((state) => state.user)
  const { groups, nowGroup, nowBrand } = user || {}
  const [sources, setSources] = useState({})
  const [selectedSource, setSelectedSource] = useState()
  const [readySetup, setReadySetup] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectorOpt, setSelectorOpt] = useState([])
  const goBack = () => navigate(goBackPath, { state: { type: 'criteria' } })

  const defPlaceholder = (name, type) => `${type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''} ${ln_t(`criteria.${name}`)}`
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`criteria.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const handleAdd = async reqBody => {
    const res = await addCriteria(reqBody)
    if (res?.status === 201) {
      message.success(ln_t('message.success.create'))
      goBack()
    }
  }
  const handleModify = async (id, reqBody) => {
    const res = await modCriteria(id, reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      goBack()
    }
  }
  const handleSave = item => {
    const { name, type, status, ...other } = item
    const { custom, error } = customReqBody[type] ? customReqBody[type](other) : customReqBody.default({ ...other, type })
    if (error) return message.error(ln_t('message.error.incompleteCriteria'))
    const reqBody = { name, type: sources[type], status, brandId: nowBrand, ...custom }
    addMode ? handleAdd(reqBody) : handleModify(data?.id, reqBody)
  }

  const handleChangeSource = e => {
    setSelectedSource(e)
    // reset fields
    const { name, logic, status } = form.getFieldsValue()
    form.resetFields()
    form.setFieldsValue({ name, type: e, logic, status })
  }

  const fetchSource = async () => {
    const availableSrc = groups?.find(i => i.id === nowGroup)?.dataSourceTypes?.split(',') || []
    const res = await fetchSources()
    if (res?.status === 200) {
      const type = res?.data?.filter(i => availableSrc.includes(`${i.id}`) && i.hasCriteria)
      setSources(type.reduce((all, value) => ({ ...all, [value.name]: value.id }), {}))
      if (editMode) {
        const typeVal = type.find(({ id }) => `${id}` === `${data?.type}`)?.name
        form.setFieldValue('type', typeVal)
        setSelectedSource(typeVal)
      }
    }
  }
  useEffect(() => {
    if ((!addMode && !editMode) || (editMode && !data)) return goBack()
    fetchSource()
    if (addMode) return
    const { name, status, rule } = data
    form.setFieldsValue({ name, status, logic: rule?.logic })
  }, [])

  // ----- get different source config (will change based on selected source) ----- //
  const [conf, setConf] = useState({ basic: basicConf.default, category: categoryConf(ln_t).default })
  const [prevLanguage, setPrevLanguage] = useState(user?.language)
  const [languageChanged, setLanguageChanged] = useState(false)

  const initFetch = async fetchFunc => {
    setIsLoading(true)
    const res = await fetchFunc(nowBrand, sources[selectedSource])
    if (res?.status === 200) setSelectorOpt(res?.data)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!selectedSource) return
    // handle basic config
    const srcConf = basicConf[selectedSource] || {}
    if (srcConf.extraSelector?.fetch) initFetch(srcConf.extraSelector.fetch)
    // handle category config and options below it
    const srcCategoryConf = categoryConf(ln_t)[selectedSource] || {}
    setConf({ ...conf, basic: { ...basicConf.default, ...srcConf }, category: { ...categoryConf(ln_t).default, ...srcCategoryConf } })
    setReadySetup(true)
  }, [selectedSource])

  useEffect(() => {
    if (user?.language === prevLanguage) return
    setPrevLanguage(user?.language)
    setLanguageChanged(true)
    setConf(prev => ({ ...prev, category: { ...categoryConf(ln_t).default, ...categoryConf(ln_t)[selectedSource] || {} } }))
  }, [user?.language])

  const settingProps = {
    form,
    editMode,
    selectedSource,
    conf,
    defConfig,
    defPlaceholder,
    selectorOpt,
    setIsLoading,
    languageChanged,
    setLanguageChanged,
    readySetup
  }

  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={goBack} />
        {ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} {ln_t('segment.criteria')}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>

        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)}>
          <SettingWrapper>
            <BasicSettingWrapper>
              <SubTitle>{ln_t('util.basicSetting')}</SubTitle>
              <Form.Item {...defConfig('name', 'input')}>
                <Input placeholder={defPlaceholder('name', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('type', 'select')}>
                <Select
                  showSearch
                  placeholder={defPlaceholder('type', 'select')}
                  options={Object.keys(sources).map(val => ({ value: val, label: val }))}
                  optionFilterProp="children"
                  value={selectedSource}
                  disabled={editMode}
                  onChange={e => handleChangeSource(e)}
                />
              </Form.Item>
              {conf?.basic?.logic
                ? <Form.Item {...defConfig('logic', 'select')} initialValue="AND">
                    <Radio.Group
                      options={[{ value: 'AND', label: ln_t('criteria.and') }, { value: 'OR', label: ln_t('criteria.or') }]}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                : ''}
              <Form.Item {...defConfig('status', 'select')} initialValue={1}>
                <Radio.Group
                  options={[{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </BasicSettingWrapper>

            <CustomSettingWrapper>
              <SubTitle>{ln_t('segment.criteria')} {ln_t('util.setting')}</SubTitle>
              {selectedSource
                ? isLoading
                  ? <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: '60px' }} />
                  : <>
                  <CriteriaSetting {...settingProps} />
                  <FormButton>
                    <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
                    <Button type="primary" htmlType="submit">{ln_t('util.confirm')}</Button>
                  </FormButton>
                  </>
                : <Empty className="mt-5" description={ln_t('criteria.selectSourceToSetting')} />
              }
            </CustomSettingWrapper>
          </SettingWrapper>
        </Form>

      </ConfigProvider>
    </>
  )
}

export default CriteriaForm
